/**
 * The small icon bar on article and event pages: Facebook, LinkedIn, YouTube and email. Each icon
 * is a plain link (`next={false}`) to Ryerson's own channel or contact address; nothing here shares
 * the current page.
 *
 * Callers: `components/NewsEvents/News/NewsArticle.tsx`,
 * `components/NewsEvents/Events/EventDetails.tsx` and `components/Blog/Blog.tsx`, each as a
 * horizontal bar and, above `lg`, also as a vertical bar beside the body that follows the scroll.
 * None passes `content`, so every bar shows `./Content.tsx` → `SocialMediaContent`.
 */
import React from "react";
import styled from "@emotion/styled";
import { SocialMediaContent } from "./Content";
import { css } from "@emotion/react";
import { Icon } from "@ryerson/frontend.assets";
import { useTheme } from "@ryerson/frontend.theme";
import { Link } from "@ryerson/frontend.navigation";

/** Link targets for the bar. `instagram` is part of the shape but has no icon. */
export type SocialMediaBarItems = {
	facebook: string;
	instagram: string;
	linkedIn: string;
	/** A `mailto:` URL. */
	email: string;
	youtube: string;
};

/**
 * Props of the bar. `color` overrides the icon colour, which is otherwise
 * `theme.colors[type].text`.
 */
export type SocialMediaBarProps = {
	orientation: "horizontal" | "vertical";
	type: "primary" | "secondary" | "tertiary";
	color?: string;
	/** Absent: `./Content.tsx` → `SocialMediaContent`. */
	content?: SocialMediaBarItems;
};

const SocialMediaContainer = styled.div`
	display: block;
	text-align: center;
`;

const SocialIcon = styled.div`
	display: inline-block;
	box-sizing: border-box;
	line-height: 20px;
`;

/** Renders the four icon links, side by side or stacked per `orientation`. */
const SocialMediaComponent: React.FC<SocialMediaBarProps> = ({
	orientation = "horizontal",
	content,
	color,
	type = "primary",
}) => {
	const { theme } = useTheme();
	if (!content) content = SocialMediaContent;
	const orientationIconStyling = css`
		margin-right: ${orientation === "vertical" ? "0px" : "12px"};
		margin-bottom: ${orientation === "vertical" ? "6px" : "0px"};
	`;
	const iconStyling = css`
		cursor: pointer;
	`;
	return (
		<SocialMediaContainer
			css={css`
				height: ${orientation === "vertical" ? "auto" : "20px"};
				width: ${orientation === "vertical" ? "20px" : "100%"};
				text-align: ${orientation === "vertical" ? "center" : "left"};
			`}
		>
			<SocialIcon css={orientationIconStyling}>
				<Link to={content.facebook} next={false}>
					<Icon
						css={iconStyling}
						icon="social-facebook"
						size="sm"
						color={color ? color : theme.colors[type].text}
					/>
				</Link>
			</SocialIcon>

			<SocialIcon css={orientationIconStyling}>
				<Link to={content.linkedIn} next={false}>
					<Icon
						css={iconStyling}
						icon="social-linked-in"
						size="sm"
						color={color ? color : theme.colors[type].text}
					/>
				</Link>
			</SocialIcon>
			<SocialIcon css={orientationIconStyling}>
				<Link to={content.youtube} next={false}>
					<Icon
						css={iconStyling}
						icon="social-youtube"
						size="sm"
						color={color ? color : theme.colors[type].text}
					/>
				</Link>
			</SocialIcon>
			<SocialIcon css={orientationIconStyling}>
				<Link to={content.email} next={false}>
					<Icon
						css={iconStyling}
						icon="mail"
						size="sm"
						color={color ? color : theme.colors[type].text}
					/>
				</Link>
			</SocialIcon>
		</SocialMediaContainer>
	);
};

export default SocialMediaComponent;
